import React, { useState, useEffect } from 'react';
import Layout from '../layouts/Layout';
import { FaMoneyBillWave, FaCalendar, FaClipboardList, FaEdit, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import './LoanRequest.css';

const LoanRequest = () => {
  const [formData, setFormData] = useState({
    type: '',
    amount: '',
    repayment_period: '',
    reason: '',
    status: 'pending',
  });
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const baseUrl = "https://api.burrayhr.com";

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/profile/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log("user profile",response.data);
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      showAlert('error', 'Failed to load user profile');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'amount') {
      // Only allow numbers and limit to 2 decimal places
      const regex = /^\d*\.?\d{0,2}$/;
      if (value === '' || regex.test(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else if (name === 'repayment_period') {
      // Only allow positive integers for days
      const days = parseInt(value);
      if (!isNaN(days) && days >= 0) {
        setFormData(prev => ({
          ...prev,
          [name]: days.toString()
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const showAlert = (type, message) => {
    Swal.fire({
      icon: type,
      title: type === 'success' ? 'Success!' : 'Error!',
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      background: type === 'success' ? '#d4edda' : '#f8d7da',
      color: type === 'success' ? '#155724' : '#721c24',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userProfile?.id) {
      showAlert('error', 'User profile not loaded. Please try again.');
      return;
    }

    if (!formData.type) {
      showAlert('error', 'Please select a request type');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showAlert('error', 'Please enter a valid amount');
      return;
    }

    if (!formData.repayment_period || parseInt(formData.repayment_period) <= 0) {
      showAlert('error', 'Please enter a valid repayment period');
      return;
    }

    if (!formData.reason.trim()) {
      showAlert('error', 'Please provide a reason for your request');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const requestData = {
        ...formData,
        status: 'pending',
        is_paid: false
      };
      
      await axios.post(`${baseUrl}/api/loans/${userProfile.employee_data.id}/`, requestData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      showAlert('success', 'Loan request submitted successfully!');
      
      setFormData({
        type: '',
        amount: '',
        repayment_period: '',
        reason: ''
      });
    } catch (error) {
      console.error('Error submitting loan request:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit loan request';
      showAlert('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!userProfile) {
    return (
      <Layout>
        <div className="loan-request-container">
          <div className="loading-state">
            <FaSpinner className="spinner" />
            <p>Loading user profile...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="loan-request-container">
        <div className="loan-request-header">
          <FaMoneyBillWave className="header-icon" />
          <h2>Loan Request Form</h2>
        </div>

        <div className="loan-request-card">
          <form onSubmit={handleSubmit} className="loan-request-form">
            <div className="form-group">
              <label>
                <FaClipboardList className="field-icon" />
                Request Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                className="form-select"
              >
                <option value="">Select Request Type</option>
                <option value="loan">Loan</option>
                <option value="advance">Salary Advance</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <FaMoneyBillWave className="field-icon" />
                Amount ($)
              </label>
              <input
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>
                <FaCalendar className="field-icon" />
                Repayment Period (days)
              </label>
              <input
                type="number"
                name="repayment_period"
                value={formData.repayment_period}
                onChange={handleChange}
                placeholder="Enter repayment period in days"
                required
                min="1"
                max="365"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>
                <FaEdit className="field-icon" />
                Reason for Request
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Please provide a reason for your request"
                required
                className="form-textarea"
                rows="4"
              />
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>

      <style jsx>{`
        .loan-request-container {
          padding: 2rem;
          max-width: 800px;
          margin: 0 auto;
        }

        .loan-request-header {
          display: flex;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e5e7eb;
        }

        .header-icon {
          font-size: 2rem;
          color: #1e3a8a;
          margin-right: 1rem;
        }

        .loan-request-header h2 {
          color: #1e3a8a;
          margin: 0;
          font-size: 1.8rem;
        }

        .loan-request-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }

        .loan-request-form {
          display: grid;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #4b5563;
          font-weight: 500;
        }

        .field-icon {
          color: #1e3a8a;
        }

        .form-select,
        .form-input {
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-select:focus,
        .form-input:focus {
          outline: none;
          border-color: #1e3a8a;
          box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
        }

        .submit-btn {
          background-color: #1e3a8a;
          color: white;
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .submit-btn:hover {
          background-color: #1e40af;
        }

        .submit-btn:disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
        }

        @media (max-width: 640px) {
          .loan-request-container {
            padding: 1rem;
          }

          .loan-request-card {
            padding: 1.5rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default LoanRequest;
